import { getGeolocalization } from "src/api/services/geolocalization/geolocalizationApi";
import { ORIGIN_PARAM_NAME, URL_REDIRECT } from 'src/constants/UrlConstants';
import {
  EntryOriginType,
  MarketApiResponse,
  ShopOriginConfiguration,
} from "src/model/shop.model";
import { LegalContent } from "src/model/state/partials/store.model";
import { getEntryISOPathExtraction, getOriginSession, hasUrlParam, isLocal, refreshUrlParams } from "../BasePathTools";
import { APP_DEFAULT_COUNTRY, APP_DEFAULT_LANGUAGE } from "../constants";
import { createCookie, getValueCookie, removeCookie } from '../cookies';
import { isCartFromPreownedPage, isUserFromCategoryMenu } from '../redirect/manager-redirect';
import { StoreService } from "../store-hooks/core/StoreService";
import { StoreConstants } from "../store-hooks/store-constants";
import { getGeoFromCookie } from "../utils";
import { ENTRY_ORIGIN_VARIANTS } from './../../model/shop.model';

const SHOP_ORIGIN_CONFIGURATION: ShopOriginConfiguration = {
  country: "",
  market: "",
  lang: "",
  entryLang: "",
  entryISO: "",
  storeId: "",
  entryOrigin: ""
};

export const getShopConfigurationObject = () => {
  return StoreService.getObject(StoreConstants.SHOP_ORIGIN_CONFIGURATION);
};

export const getEntryOrigin = (): EntryOriginType => {
  const configuration: ShopOriginConfiguration | any = StoreService.getObject(
    StoreConstants.SHOP_ORIGIN_CONFIGURATION,
  );
  return configuration?.entryOrigin;
};

export const resetConfigurationSessioObj = async () => {
  StoreService.remove(StoreConstants.SHOP_ORIGIN_CONFIGURATION);
  StoreService.setObject(
    StoreConstants.SHOP_ORIGIN_CONFIGURATION,
    SHOP_ORIGIN_CONFIGURATION,
  );
};

export const sessionOriginProcess = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const originEntry = getOriginSession();
  const originInCookies = getValueCookie(ORIGIN_PARAM_NAME)?.toUpperCase();
  const originUrlParam = ENTRY_ORIGIN_VARIANTS[originEntry];
  if (!!originUrlParam || !!originInCookies) {
    await addEntryOriginInSession(originEntry || originInCookies);
    if(!!originUrlParam){
      urlParams.delete(ORIGIN_PARAM_NAME);
      refreshUrlParams(urlParams);
    }
  }
};

export const addCountryInSession = async (country: string): Promise<void> => {
  const shopConfiguration: any = getShopConfigurationObject();
  const objectForSave: ShopOriginConfiguration = {
    ...shopConfiguration,
    ...{ country: country?.toLowerCase() },
  };
  await StoreService.setObject(
    StoreConstants.SHOP_ORIGIN_CONFIGURATION,
    objectForSave,
  );
};

export const addLanguageInSession = async (
  lang: string,
  entryParcialLang: string,
): Promise<void> => {
  const shopConfiguration: any = getShopConfigurationObject();
  const objectForSave: ShopOriginConfiguration = {
    ...shopConfiguration,
    ...{
      lang: lang?.toLowerCase() ?? APP_DEFAULT_LANGUAGE.toLowerCase(),
      entryLang: entryParcialLang,
    },
  };
  await StoreService.setObject(
    StoreConstants.SHOP_ORIGIN_CONFIGURATION,
    objectForSave,
  );
};
export const addMarketInSession = async (marketIso: string): Promise<void> => {
  const shopConfiguration: any = getShopConfigurationObject();
  const objectForSave: ShopOriginConfiguration = {
    ...shopConfiguration,
    ...{ market: marketIso?.toLowerCase() ?? shopConfiguration?.country },
  };
  await StoreService.setObject(
    StoreConstants.SHOP_ORIGIN_CONFIGURATION,
    objectForSave,
  );
};

export const addStoreIdInSession = async (storeId: string): Promise<void> => {
  const shopConfiguration: any = getShopConfigurationObject();
  const objectForSave: ShopOriginConfiguration = {
    ...shopConfiguration,
    ...{ storeId:  storeId },
  };
  await StoreService.setObject(
    StoreConstants.SHOP_ORIGIN_CONFIGURATION,
    objectForSave,
  );
};

export const addEntryISOPathInSession = async (
  entryISO: string,
): Promise<void> => {
  const shopConfiguration: any = getShopConfigurationObject();
  const objectForSave: ShopOriginConfiguration = {
    ...shopConfiguration,
    ...{ entryISO: entryISO ?? shopConfiguration?.country },
  };
  await StoreService.setObject(
    StoreConstants.SHOP_ORIGIN_CONFIGURATION,
    objectForSave,
  );
};

export const addEntryOriginInSession = async (
  entryOrigin: string,
): Promise<void> => {
  const shopConfiguration: any = getShopConfigurationObject();
  const objectForSave: ShopOriginConfiguration = {
    ...shopConfiguration,
    ...{ entryOrigin: entryOrigin ?? "" },
  };
  await StoreService.setObject(
    StoreConstants.SHOP_ORIGIN_CONFIGURATION,
    objectForSave,
  );
  createCookie(ORIGIN_PARAM_NAME, entryOrigin, null);
};

export const removeEntryOriginInSession = async (): Promise<void> => {
  const shopConfiguration: any = getShopConfigurationObject();
  const objectForSave: ShopOriginConfiguration = {
    ...shopConfiguration,
    ...{ entryOrigin: "" },
  };
  await StoreService.setObject(
    StoreConstants.SHOP_ORIGIN_CONFIGURATION,
    objectForSave,
  );
  removeCookie(ORIGIN_PARAM_NAME);
};

export const getLegalContent = (market: MarketApiResponse): LegalContent => {
  return {
    termsAndConditionsUrl: market?.termsAndConditionsUrl,
    privacyPolicyUrl: market?.privacyPolicyUrl,
    omnibusUrl: market?.omnibusUrl,
    legalText: market?.legalText,
    feedTitle: market?.legal?.feedTitle,
		feedDescription: market?.legal?.feedDescription,
		legalTitle: market?.legal?.legalTitle,
		legalDescription: market?.legal?.legalDescription,
    policies: market?.legal?.policies,
    dacEnabled: market?.legal?.dacEnabled,
    digitalServicesContact: market?.legal?.digitalServicesContact,
  };
};

export const getRestrictedAddressCodes = (
  market: MarketApiResponse,
): { statesRestricted: string[] | []; zipCodesRestricted: string[] | [] } => {
  return {
    statesRestricted: market?.statesRestricted,
    zipCodesRestricted: market?.zipCodesRestricted,
  };
};

export const getShopId = (market: MarketApiResponse): number => {
  return typeof market?.id === "string" ? Number(market.id) : null;
};

export const goToZara = (): void => {
  window.location.href = window.location.origin;
};

export const getGeoFromService = async () => {
  let geo = null;
  if (isLocal) {
    geo = APP_DEFAULT_COUNTRY;
  } else {
    geo = await getGeolocalization();
  }
  return geo;
};

export const getUserCountryIso = async () => {
  return getGeoFromCookie() || (await getGeoFromService());
};

export const proccesCountry = async (currentCountry: string) => {
  if (!!currentCountry) {
    await addCountryInSession(currentCountry);
  } else {
    goToZara();
  }
};

export const proccesLanguage = async (
  currentLanguage: string,
  entryParcialLang: string,
) => {
  addLanguageInSession(currentLanguage, entryParcialLang);
};

export const processISOMarket = (marketIso: string) => {
  addMarketInSession(marketIso);
};

export const processStoreId = (storeId: string) => {
  addStoreIdInSession(storeId);
};

export const InitialOriginPathExtraction = () => {
  const currentOriginPath = getEntryISOPathExtraction();
  addEntryISOPathInSession(currentOriginPath);
};

export const initialLanguagePathExtraction = async (
  market: MarketApiResponse,
): Promise<void> => {
  const hasLanguage = location
    ? !!location.pathname.match(/^\/.*\/.*\/preowned-resell/gm)
    : null;
  const initialParcialPathLanguage = hasLanguage
    ? location.pathname.match(/\/([^/]+)\/preowned-resell/)[1]
    : APP_DEFAULT_LANGUAGE.toLowerCase();
  const currentPathLanguage = market?.languages?.availableLanguages?.find(
    (availableLanguage) =>
      availableLanguage?.code.toLowerCase() ===
      initialParcialPathLanguage.toLocaleLowerCase(),
  );

  await proccesLanguage(
    currentPathLanguage?.code ?? market?.languages?.default,
    initialParcialPathLanguage,
  );
};

export const manageCookiesBasedOnUrl = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const redirectCookieValue = getValueCookie(URL_REDIRECT);
  const redirectUrlParam = hasUrlParam(URL_REDIRECT?.toLowerCase());

  if (!redirectCookieValue?.trim()) {
    return;
  }

  const shouldKeepRedirectCookie = isUserFromCategoryMenu() || isCartFromPreownedPage() || redirectUrlParam;
  if (shouldKeepRedirectCookie) {
    if (redirectUrlParam) {
      urlParams.delete(URL_REDIRECT?.toLowerCase());
      refreshUrlParams(urlParams);
    }
  } else {
    removeCookie(URL_REDIRECT);
  }
};
